import { Component, HostListener, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, debounceTime } from 'rxjs';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { ConfirmDialogComponent } from '../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { CouponService } from '../../_services/coupon.service';
import { AlertService } from '../../_services/alert.service';
import { Coupon, CouponStats, CouponStatus } from '../../_interfaces/coupons';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

type StatusFilter = '' | CouponStatus | 'active';
type KindFilter = '' | 'percent' | 'flat' | 'giveaway' | 'staff' | 'customer';

// Codes whose expiry is this far away are "no expiry" placeholders.
const NO_EXPIRY_YEARS = 3;
// Limits this big are how "no limit" got stored.
const NO_LIMIT = 1_000_000;

// The list as it stands, carried to a coupon's page (Previous / Next follow
// it) and back.
export interface CouponListState {
  q?: string;
  status?: string;
  kind?: string;
  sort?: string;
  page?: string;
  size?: string;
}

@Component({
    selector: 'app-discount-coupons',
    imports: [PaginatorPagesDirective, CommonModule, RouterLink, MainLayoutComponent, DatePipe, FormsModule, MatIconModule, MatTooltipModule, MatPaginatorModule, MatProgressSpinnerModule],
    templateUrl: './discount-coupons.component.html',
    styleUrl: './discount-coupons.component.css'
})
export class DiscountCouponsComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly couponService = inject(CouponService);
  private readonly alertService = inject(AlertService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly Math = Math;

  // The slide-over panel.
  selected: Coupon | null = null;

  coupons: Coupon[] = [];
  count = 0;
  stats: CouponStats | null = null;
  pageIndex = 0;
  pageSize = 10;
  couponKeyword = '';
  status: StatusFilter = '';
  kind: KindFilter = '';
  sort = 'newest';
  loading = true;
  loadError = false;
  copied = '';

  readonly statusOptions: { value: StatusFilter; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'expiring', label: 'Expiring soon' },
    { value: 'usedUp', label: 'Used up' },
    { value: 'expired', label: 'Expired' },
  ];
  readonly kindOptions: { value: KindFilter; label: string }[] = [
    { value: '', label: 'Any type' },
    { value: 'percent', label: 'Percentage' },
    { value: 'flat', label: 'Fixed amount' },
    { value: 'giveaway', label: 'Free gift' },
    { value: 'staff', label: 'Staff only' },
    { value: 'customer', label: 'For one customer' },
  ];
  readonly sortOptions = [
    { value: 'newest', label: 'Newest first' },
    { value: 'mostUsed', label: 'Most used' },
    { value: 'lastUsed', label: 'Used most recently' },
    { value: 'expiry', label: 'Expiry date' },
    { value: 'value', label: 'Biggest discount' },
  ];

  private readonly search$ = new Subject<void>();
  private loadSeq = 0;

  constructor() {
    this.search$.pipe(debounceTime(350), takeUntilDestroyed()).subscribe(() => {
      this.pageIndex = 0;
      this.load();
    });
  }

  ngOnInit(): void {
    // Back from a coupon's page: the list as it was.
    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('q')) this.couponKeyword = qp.get('q')!;
    if (qp.get('status')) this.status = qp.get('status') as StatusFilter;
    if (qp.get('kind')) this.kind = qp.get('kind') as KindFilter;
    if (qp.get('sort')) this.sort = qp.get('sort')!;
    if (qp.get('page')) this.pageIndex = Math.max(0, Number(qp.get('page')) || 0);
    if (qp.get('size')) this.pageSize = Math.max(1, Number(qp.get('size')) || 10);
    this.load();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.selected = null;
  }

  get listState(): CouponListState {
    const s: CouponListState = {};
    if (this.couponKeyword.trim()) s.q = this.couponKeyword.trim();
    if (this.status) s.status = this.status;
    if (this.kind) s.kind = this.kind;
    if (this.sort !== 'newest') s.sort = this.sort;
    if (this.pageIndex) s.page = String(this.pageIndex);
    if (this.pageSize !== 10) s.size = String(this.pageSize);
    return s;
  }

  view(c: Coupon): void {
    this.selected = this.selected?.id === c.id ? null : c;
  }

  includedCount(list: { include?: boolean | number }[]): number {
    return list.filter((x) => x.include === undefined || !!x.include).length;
  }

  get maxUsed(): number {
    return Math.max(1, ...this.coupons.map((c) => c.used ?? 0));
  }

  get filtersActive(): boolean {
    return !!(this.couponKeyword || this.status || this.kind);
  }

  countFor(value: StatusFilter): number {
    const s = this.stats;
    if (!s) return 0;
    switch (value) {
      case '': return s.total;
      case 'active': return s.active;
      case 'expiring': return s.expiring;
      case 'usedUp': return s.usedUp;
      case 'expired': return s.expired;
    }
  }

  load(): void {
    const seq = ++this.loadSeq;
    this.loading = true;
    this.couponService
      .getAllCoupons({
        keywordSearch: this.couponKeyword.trim(),
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        status: this.status,
        kind: this.kind,
        sort: this.sort,
      })
      .subscribe({
        next: (response: any) => {
          if (seq !== this.loadSeq) return;
          this.coupons = response.couponCodes ?? [];
          this.count = response.count ?? 0;
          if (response.stats) this.stats = response.stats;
          this.loading = false;
          this.loadError = false;
        },
        error: (error) => {
          if (seq !== this.loadSeq) return;
          this.loading = false;
          this.loadError = true;
          this.alertService.alert(error.error?.message || 'Could not load coupons', 'error');
        },
      });
  }

  onSearchInput(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.couponKeyword = '';
    this.pageIndex = 0;
    this.load();
  }

  setStatus(value: StatusFilter): void {
    this.status = this.status === value ? '' : value;
    this.pageIndex = 0;
    this.load();
  }

  setKind(value: KindFilter): void {
    this.kind = value;
    this.pageIndex = 0;
    this.load();
  }

  setSort(value: string): void {
    this.sort = value;
    this.pageIndex = 0;
    this.load();
  }

  resetFilters(): void {
    this.couponKeyword = '';
    this.status = '';
    this.kind = '';
    this.pageIndex = 0;
    this.load();
  }

  onPage(event: PageEvent): void {
    if (this.pageSize !== event.pageSize) {
      this.pageSize = event.pageSize;
      this.pageIndex = 0;
    } else {
      this.pageIndex = event.pageIndex;
    }
    this.load();
  }

  // ---- What a row shows ----

  valueLabel(c: Coupon): string {
    const v = Number(c.couponValue) || 0;
    const text = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return c.couponPerc ? `${text}%` : `£${text}`;
  }

  // "Never" for the far-off placeholder dates, else how long is left / since.
  expiryInfo(c: Coupon): { label: string; note: string } {
    if (!c.expiryDate) return { label: 'No expiry', note: '' };
    const date = new Date(c.expiryDate);
    const days = Math.ceil((date.getTime() - Date.now()) / 86400000);
    if (days > NO_EXPIRY_YEARS * 365) return { label: 'No expiry', note: '' };
    if (days < 0) return { label: 'Expired', note: this.ago(-days) };
    if (days === 0) return { label: 'Expires today', note: '' };
    return { label: 'Expires', note: days === 1 ? 'tomorrow' : `in ${days} days` };
  }

  hasRealExpiry(c: Coupon): boolean {
    return !!c.expiryDate && this.expiryInfo(c).label !== 'No expiry';
  }

  ago(days: number): string {
    if (days < 1) return 'today';
    if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.round(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
    const years = Math.round(days / 365);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  }

  lastUsedAgo(c: Coupon): string {
    if (!c.lastUsed) return '';
    const days = Math.floor((Date.now() - new Date(c.lastUsed).getTime()) / 86400000);
    return this.ago(Math.max(0, days));
  }

  limited(limit: number | null | undefined): boolean {
    return !!limit && limit > 0 && limit < NO_LIMIT;
  }

  usagePercent(c: Coupon): number {
    if (!this.limited(c.useTimes)) return 0;
    return Math.min(100, Math.round(((c.used ?? 0) / c.useTimes) * 100));
  }

  statusStyle(c: Coupon): { label: string; cls: string; dot: string } {
    switch (c.status) {
      case 'expired': return { label: 'Expired', cls: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
      case 'usedUp': return { label: 'Used up', cls: 'bg-rose-50 text-rose-700', dot: 'bg-rose-500' };
      case 'expiring': return { label: 'Expiring soon', cls: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' };
      default: return { label: 'Active', cls: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' };
    }
  }

  isLive(c: Coupon): boolean {
    return c.status === 'active' || c.status === 'expiring';
  }

  rules(c: Coupon): { icon: string; text: string; tone?: string }[] {
    const out: { icon: string; text: string; tone?: string }[] = [];
    if (Number(c.minSpend) > 0) out.push({ icon: 'shopping_basket', text: `Min spend £${c.minSpend}` });
    if (Number(c.maxSpend) > 0) out.push({ icon: 'vertical_align_top', text: `Max spend £${c.maxSpend}` });
    if (Number(c.useTimesCustomer) === 1) out.push({ icon: 'person', text: 'Once per customer' });
    else if (this.limited(c.useTimesCustomer)) out.push({ icon: 'person', text: `${c.useTimesCustomer} uses per customer` });
    if (c.customer) out.push({ icon: 'person_pin', text: `${c.customer.firstname ?? ''} ${c.customer.lastname ?? ''}`.trim() });
    if (c.excludeSales) out.push({ icon: 'sell', text: 'Not on sale items' });
    if (c.individualUse) out.push({ icon: 'block', text: 'Cannot combine' });
    const cats = (c.couponCategories ?? []).length;
    const prods = (c.couponProducts ?? []).length;
    if (cats) out.push({ icon: 'category', text: `${cats} categor${cats === 1 ? 'y' : 'ies'}` });
    if (prods) out.push({ icon: 'inventory_2', text: `${prods} product${prods === 1 ? '' : 's'}` });
    if (c.giveAway) out.push({ icon: 'redeem', text: 'Free gift', tone: 'violet' });
    if (Number(c.internalUseOnly) === 1) out.push({ icon: 'badge', text: 'Staff only', tone: 'sky' });
    try {
      const roles = c.userRoles ? JSON.parse(c.userRoles) : [];
      if (roles.length) out.push({ icon: 'workspace_premium', text: roles.join(', ') });
    } catch { /* unreadable roles: skip */ }
    return out;
  }

  // ---- Actions ----

  copyCode(c: Coupon): void {
    navigator.clipboard?.writeText(c.couponCode).then(() => {
      this.copied = c.couponCode;
      setTimeout(() => { if (this.copied === c.couponCode) this.copied = ''; }, 1500);
    });
  }

  // Create / edit / duplicate happen on the coupon's own page.
  onEditClick(type: string, rowData?: Coupon): void {
    if (type === 'EDIT' && rowData) this.router.navigate(['/discount-coupons/edit', rowData.id], { queryParams: this.listState });
    else this.router.navigate(['/discount-coupons/create'], { queryParams: this.listState });
  }

  onDuplicateClick(c: Coupon): void {
    this.router.navigate(['/discount-coupons/create'], { queryParams: { ...this.listState, copy: c.id } });
  }

  onDeleteClick(c: Coupon): void {
    const used = c.used ?? 0;
    const message = used > 0
      ? `Delete "${c.couponCode}"? It has been used ${used} time${used === 1 ? '' : 's'}; those orders keep their discount but the code will stop working.`
      : `Delete "${c.couponCode}"? The code will stop working.`;
    this.dialog
      .open(ConfirmDialogComponent, { width: '460px', maxWidth: '96vw', data: { title: 'Delete coupon', message } })
      .afterClosed()
      .subscribe((result) => {
        if (result?.confirmation) this.deleteCoupon(c);
      });
  }

  private deleteCoupon(c: Coupon): void {
    this.couponService.deleteCouponCode(c.id).subscribe({
      next: () => {
        this.alertService.alert('Coupon deleted', 'success');
        if (this.selected?.id === c.id) this.selected = null;
        this.load();
      },
      error: (error) => this.alertService.alert(error.error?.message || 'Could not delete the coupon', 'error'),
    });
  }
}
